import React from "react";
import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionCard,
  DecisionList,
} from "@/components/DecisionPageLayout";
import { Users, Heart, MessageSquare, ShieldCheck } from "lucide-react";
import { InlineRelatedLinks } from "@/components/RelatedPages";

const ForFamilies = () => (
  <DecisionPageLayout
    seo={{
      title: "For Families & Caregivers | Neuro Wellness TMS",
      description:
        "Information for family members and caregivers supporting someone considering SAINT TMS therapy — how to bring it up, what to expect, and what support looks like.",
      canonical: "/for-families",
      keywords:
        "family support TMS, caregiver SAINT therapy, helping someone with depression, family TMS treatment",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: "For Families and Caregivers",
        about:
          "Guidance for family members supporting a patient considering SAINT TMS therapy",
      },
    }}
    badge="For Families"
    title="For Families and Caregivers"
    subtitle="If you're here on behalf of someone you care about, this is for you."
  >
    <DecisionSection heading="If You're Inquiring for Someone Else">
      <p>
        Many of our first calls come from a spouse, a parent, an adult child, or
        a close friend — not the person who will receive treatment. That's
        normal. Depression makes it hard to take the first step, and the people
        who love someone in that state often step in.
      </p>
      <p>
        You can gather information, ask questions, and even schedule the
        consultation. But the person receiving treatment must be the one who
        consents and participates.
      </p>
    </DecisionSection>

    <DecisionCard>
      <div className="flex items-center gap-3">
        <MessageSquare className="h-6 w-6 text-accent shrink-0" />
        <DecisionSubHeading>How to Bring It Up</DecisionSubHeading>
      </div>
      <p>
        You don't need to make the case for SAINT. You can simply say: "I found
        a center in Coral Springs that does a five-day treatment for depression
        that hasn't responded to medication. Would you be willing to look at it
        with me?"
      </p>
      <p>
        If they're not ready, that's okay. The information will be here when
        they are. Pressuring someone into treatment rarely works, and it's not
        how we operate.
      </p>
    </DecisionCard>

    <DecisionSection>
      <DecisionSubHeading>
        What to Expect During the Treatment Week
      </DecisionSubHeading>
      <div className="flex gap-3 items-start">
        <Users className="h-5 w-5 text-accent shrink-0 mt-1" />
        <p>
          Family members may accompany the patient during the treatment week.
          You can be present in our suite area while the patient is in sessions.
          Many families use the week as a shared experience — staying at the
          same hotel, having meals together, being available between sessions.
        </p>
      </div>
    </DecisionSection>

    <DecisionSection>
      <DecisionSubHeading>What Support Looks Like Afterward</DecisionSubHeading>
      <div className="flex gap-3 items-start">
        <Heart className="h-5 w-5 text-accent shrink-0 mt-1" />
        <p>
          After the five-day treatment, the patient returns home. The most
          helpful thing you can do is be steady — not hovering, not asking "do
          you feel better?" every hour. Let them tell you what they notice.
          Follow-up appointments are scheduled by our team, and ongoing
          communication with the patient's existing therapist or psychiatrist is
          encouraged.
        </p>
      </div>
    </DecisionSection>

    <DecisionSection>
      <DecisionSubHeading>
        The Patient Must Consent and Participate
      </DecisionSubHeading>
      <div className="flex gap-3 items-start">
        <ShieldCheck className="h-5 w-5 text-accent shrink-0 mt-1" />
        <p>
          SAINT is a voluntary treatment. The patient must understand what it
          involves and agree to participate. No one can be enrolled by a family
          member. If the person you're concerned about is not willing, the
          consultation can still happen — but treatment will not proceed without
          their informed consent.
        </p>
      </div>
    </DecisionSection>

    <DecisionSection>
      <DecisionSubHeading>
        If You're the Patient's Support Person
      </DecisionSubHeading>
      <DecisionList
        items={[
          "You're welcome to join the consultation call if the patient agrees.",
          "You can help gather records and insurance information.",
          "You can arrange travel and accommodation if the patient is coming from out of town.",
          "You can be present during the treatment week — but not in the treatment room itself.",
          "You can help the patient maintain their regular medication and therapy routine afterward.",
        ]}
      />
    </DecisionSection>

    <InlineRelatedLinks
      heading="Explore Related Pages"
      links={[
        { title: "SAINT® Accelerated TMS", href: "/saint" },
        { title: "Traveling for Treatment", href: "/traveling-for-treatment" },
        { title: "Am I a Candidate?", href: "/am-i-a-candidate" },
        { title: "How Insurance Works", href: "/how-insurance-works" },
        { title: "Compare All Treatments", href: "/compare-treatments" },
      ]}
    />
  </DecisionPageLayout>
);

export default ForFamilies;
